import DropdownSelector from 'components/DropdownSelector';
import { RequiredFieldIndicator } from 'features/Responder/components/form fields';
import React from 'react';
import { Button, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import {
  formatRruleText,
  generateEventsBetween,
  getFixedDtstart,
  getScheduleEventProps,
} from 'utils/recurrenceUtils';
import { RRuleBuilder } from 'features/Responder/features/Schedules/features/ScheduleBuilder/components/RruleBuilder';
import ScheduleCalendar from 'features/Responder/features/Schedules/features/ScheduleBuilder/components/RruleBuilder/components/ScheduleCalendar';

const ScheduleBuilderView = ({
  activeEditSchedule,
  scheduleName,
  setScheduleName,
  shouldShowErrorIndicators,
  hasNameError,
  tourOptions,
  selectedTour,
  selectTourOption,
  hasTargetError,
  selectedZone,
  zoneOptions,
  selectZoneOption,
  rrule,
  setRrule,
  startTime,
  scheduleDuration,
  endTime,
  useTourDuration,
  notAllDayTimeStart,
  setNotAllDayTimeStart,
  setUseTourDuration,
  setScheduleDuration,
  setStartTime,
  setEndTime,
  setOldTimeSlot,
  setOldScheduleDuration,
  oldTimeSlot,
  oldScheduleDuration,
  isZoneSelected,
  tours,
  getTotalDurationOfTour,
  hasTimeError,
  isTargetSelected,
  startTimeField,
  endTimeField,
  extraStartTimes,
  setExtraStartTimes,
  notes,
  setNotes,
  colorMap,
  color,
  setColor,
  handleCancel,
  handleClearSelection,
  showSubmitTooltip,
  scheduleScrollRef,
  handleScheduleTour,
  currEditData,
  dialogElement,
}) => (
  <div className="p-3 rounded-2 h-100 d-flex flex-column" style={{ background: '#0b1826' }}>
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="w-100">
        <h4 className="text-white mb-0">Schedule Builder</h4>
        <Form.Group className="mt-3">
          <div className="d-flex align-items-center">
            <Form.Label className="fw-bold text-white mb-1">Schedule Name</Form.Label>
            <RequiredFieldIndicator
              showError={shouldShowErrorIndicators && hasNameError}
              message="Please enter a schedule name"
            />
          </div>
          <Form.Control
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            placeholder="Enter schedule name..."
            className="bg-dark text-white border-secondary"
          />
        </Form.Group>
        {activeEditSchedule && (
          <div className="text-info small mt-1">
            Editing schedule{activeEditSchedule.id ? ` (ID: ${activeEditSchedule.id})` : ''}
          </div>
        )}
      </div>
    </div>

    <div className="overflow-auto flex-grow-1" style={{ minHeight: 0 }} ref={scheduleScrollRef}>
      <div className="mb-4">
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Group>
              <div className="d-flex align-items-center">
                <Form.Label className="fw-bold text-white mb-2">Select Tour</Form.Label>
                <RequiredFieldIndicator
                  showError={shouldShowErrorIndicators && hasTargetError && selectedZone === 0}
                  message="Please select a tour or zone"
                />
              </div>
              <DropdownSelector
                options={tourOptions}
                selectedOption={selectedTour}
                setSelected={selectTourOption}
                closeOnSelect
                multiple={false}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <div className="d-flex align-items-center">
                <Form.Label className="fw-bold text-white mb-2">Select Zone</Form.Label>
                <RequiredFieldIndicator
                  showError={shouldShowErrorIndicators && hasTargetError && selectedTour === 0}
                  message="Please select a tour or zone"
                />
              </div>
              <DropdownSelector
                options={zoneOptions}
                selectedOption={selectedZone}
                setSelected={selectZoneOption}
                closeOnSelect
                multiple={false}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
      {selectedTour <= 0 && selectedZone <= 0 && (
        <div className="text-warning mb-3">
          Please select either a tour or a zone to configure the schedule.
        </div>
      )}

      {(selectedTour > 0 || selectedZone > 0) && (
        <div className="mb-4">
          <Form.Group className="mb-3">
            {rrule && rrule.toString()}
            <RRuleBuilder
              setR={setRrule}
              R={rrule}
              activeEditSchedule={activeEditSchedule}
              startTime={startTime}
              scheduleDuration={scheduleDuration}
              endTime={endTime}
              useTourDuration={useTourDuration}
              notAllDayTimeStart={notAllDayTimeStart}
              setNotAllDayTimeStart={setNotAllDayTimeStart}
              setUseTourDuration={setUseTourDuration}
              setScheduleDuration={setScheduleDuration}
              setStartTime={setStartTime}
              setEndTime={setEndTime}
              setOldTimeSlot={setOldTimeSlot}
              setOldScheduleDuration={setOldScheduleDuration}
              oldTimeSlot={oldTimeSlot}
              oldScheduleDuration={oldScheduleDuration}
              isZoneSelected={isZoneSelected}
              selectedTour={selectedTour}
              tours={tours}
              getTotalDurationOfTour={getTotalDurationOfTour}
              shouldShowErrorIndicators={shouldShowErrorIndicators}
              hasTimeError={hasTimeError}
              isTargetSelected={isTargetSelected}
              startTimeField={startTimeField}
              endTimeField={endTimeField}
              extraStartTimes={extraStartTimes}
              setExtraStartTimes={setExtraStartTimes}
            />
          </Form.Group>
        </div>
      )}

      <div className="mb-4">
        <Form.Group>
          <Form.Label className="fw-bold text-white mb-2">Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes for this schedule..."
            className="bg-dark text-white border-secondary"
          />
        </Form.Group>
      </div>

      <div className="mb-4">
        <Form.Group>
          <Form.Label className="fw-bold text-white mb-2">Color (optional)</Form.Label>
          <div>
            {Object.entries(colorMap).map(([key, colorObj]) => {
              if (!colorObj) return null;
              const isSelected = color === key;
              return (
                <Button
                  key={colorObj.bgColor}
                  style={{
                    backgroundColor: colorObj.bgColor,
                    border: isSelected
                      ? '3px solid #fff'
                      : colorObj.bgColor === '#ffffff'
                        ? '1px solid #ccc'
                        : 'none',
                    width: '32px',
                    height: '32px',
                    marginRight: '8px',
                    boxSizing: 'border-box',
                  }}
                  onClick={() => {
                    setColor(key);
                  }}
                />
              );
            })}
            <Button
              key={'none'}
              style={{
                backgroundColor: 'transparent',
                border: color === null ? '3px solid #fff' : '2px dashed #ccc',
                width: '32px',
                height: '32px',
                marginRight: '8px',
                boxSizing: 'border-box',
                position: 'relative',
                padding: 0,
              }}
              onClick={() => {
                setColor(null);
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" style={{ pointerEvents: 'none' }}>
                  <line x1="3" y1="3" x2="17" y2="17" stroke="#ccc" strokeWidth="2" />
                  <line x1="17" y1="3" x2="3" y2="17" stroke="#ccc" strokeWidth="2" />
                </svg>
              </span>
            </Button>
          </div>
        </Form.Group>
      </div>

      <div className="d-flex justify-content-end gap-3 mt-4">
        <Button variant="outline-secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="outline-light" onClick={handleClearSelection}>
          Clear Selection
        </Button>
        <OverlayTrigger
          placement="top"
          show={showSubmitTooltip}
          container={scheduleScrollRef.current}
          overlay={<Tooltip className="responder-tooltip">Please fill in the required fields</Tooltip>}
        >
          <Button variant="primary" onClick={handleScheduleTour}>
            {activeEditSchedule ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </OverlayTrigger>
      </div>

      {(selectedTour > 0 || selectedZone > 0) && (
        <div className="mt-4">
          <div className="bg-dark rounded-2 p-3">
            <h6 className="text-muted mb-3">Selection Summary:</h6>
            {selectedTour > 0 && (
              <p className="mb-2 text-white">
                <strong>Tour:</strong> {tourOptions[selectedTour]}
              </p>
            )}
            {selectedZone > 0 && (
              <p className="mb-2 text-white">
                <strong>Zone:</strong> {zoneOptions[selectedZone]}
              </p>
            )}
            {activeEditSchedule && rrule && typeof rrule.between === 'function' && (
              <p className="mb-2 text-white">
                <strong>Recurrence:</strong>{' '}
                {rrule.options && rrule.options.count === 1
                  ? 'One time'
                  : formatRruleText(rrule.toString())}
              </p>
            )}

            {currEditData && rrule && typeof rrule.between === 'function' && (
              <div className="mt-4">
                <ScheduleCalendar
                  height={500}
                  events={generateEventsBetween(
                    currEditData,
                    getFixedDtstart(rrule.options),
                    new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
                  )}
                  eventPropGetter={(event) => {
                    let props = getScheduleEventProps(event, activeEditSchedule);
                    return props;
                  }}
                  showDaily
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    {dialogElement}
  </div>
);

export default ScheduleBuilderView;
